import { useEffect, useState, useRef } from "react";
import { ClipLoader } from "react-spinners";
import { Lock, MessageCircle, Download, AlertTriangle, FolderLock } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { IoMdPlay, IoIosPause, IoMdArrowBack } from "react-icons/io";
import { HiMiniSpeakerWave, HiMiniSpeakerXMark } from "react-icons/hi2";
import { FaTrashAlt } from "react-icons/fa";
import { getDraftById, updateDraft, publishDraft, deleteDraft, setUploadProgress } from "../../redux/ApiSlice/DraftSlice";
import { getPrivateHas } from "../../redux/ApiSlice/AccountPrivateSlice";
import { addPendingUpload } from "../../redux/ApiSlice/UploadVideoSlice";

const DraftVideosDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { draftVideos, loading, uploadProgress } = useSelector((state) => state.draft);
    const { hasPin } = useSelector((state) => state.AccountPrivate);

    const videoRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const [count, setCount] = useState(0);
    const [description, setDescription] = useState("");
    const [showPrivatePopup, setShowPrivatePopup] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categorySearch, setCategorySearch] = useState("");

    // Upload Settings
    const [isCheckedPrivate, setIsCheckedPrivate] = useState(false);
    const [isCheckedComment, setIsCheckedComment] = useState(true);
    const [isCheckedDownload, setIsCheckedDownload] = useState(true);
    const [showControls, setShowControls] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const [selectedInterest, setSelectedInterest] = useState("");
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        dispatch(getDraftById(id));
        dispatch(setUploadProgress(0));
        dispatch(getPrivateHas());
    }, [dispatch, id]);

    useEffect(() => {
        if (draftVideos) {
            setDescription(draftVideos.post_description || "");
            setCount(draftVideos.post_description ? draftVideos.post_description.length : 0);
            setIsCheckedPrivate(draftVideos.is_private || false);
            setSelectedInterest(draftVideos.interest || "");

            if (draftVideos.comments_enabled !== undefined) {
                setIsCheckedComment(draftVideos.comments_enabled);
            }
            if (draftVideos.download_enabled !== undefined) {
                setIsCheckedDownload(draftVideos.download_enabled);
            }
        }
    }, [draftVideos]);

    const handleChange = (event) => {
        const inputText = event.target.value;
        setDescription(inputText);
        setCount(inputText.length);
    };

    const handleCheckboxPrivate = () => {
        if (!hasPin && !isCheckedPrivate) {
            setShowPrivatePopup(true);
            return;
        }
        setIsCheckedPrivate(!isCheckedPrivate);
    };

    const handleCheckboxComment = () => {
        setIsCheckedComment((prev) => !prev);
    };

    const handleCheckboxDownload = () => {
        setIsCheckedDownload((prev) => !prev);
    };

    const handleUpdateDraft = async () => {
        if (isUpdating || isPublishing) return;

        const updateData = {
            id: id,
            body: description,
            video: null,
            is_private: isCheckedPrivate,
            interest: selectedInterest,
            comments_enabled: isCheckedComment,
            download_enabled: isCheckedDownload
        };

        try {
            setIsUpdating(true);
            await dispatch(updateDraft(updateData)).unwrap();
            navigate('/draft-videos');
        } catch (error) {
            console.error('Failed to update draft:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePublishDraft = async () => {
        if (isPublishing || (uploadProgress > 0 && uploadProgress < 100)) return;

        if (isCheckedPrivate && !hasPin) {
            setShowPrivatePopup(true);
            return;
        }

        const publishData = {
            id: id,
            body: description,
            video: null,
            is_private: isCheckedPrivate,
            interest: selectedInterest,
            comments_enabled: isCheckedComment,
            download_enabled: isCheckedDownload
        };

        try {
            setIsPublishing(true);
            const publishedPost = await dispatch(publishDraft(publishData)).unwrap();
            dispatch(addPendingUpload(publishedPost));
            navigate('/account', { replace: true });
        } catch (error) {
            console.error('Failed to publish draft:', error);
            setIsPublishing(false);
        }
    };

    const handleDeleteDraft = async () => {
        try {
            await dispatch(deleteDraft(id)).unwrap();
            navigate('/draft-videos');
        } catch (error) {
            console.error('Failed to delete draft:', error);
        }
    };

    const handlePlayPause = (e) => {
        e.stopPropagation();
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleMuteUnmute = (e) => {
        e.stopPropagation();
        setIsMuted((prev) => {
            if (videoRef.current) {
                videoRef.current.muted = !prev;
            }
            return !prev;
        });
    };

    const handleSubmit = () => {
        handlePublishDraft();
    };

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);

    const catogary = [
        { id: 1, value: "WHEELED ADVENTURES" },
        { id: 2, value: "FUNNY" },
        { id: 3, value: "EDUCATION" },
        { id: 4, value: "ENTERTAINMENT" },
        { id: 5, value: "FILM & ANIMATION" },
        { id: 6, value: "GAMING" },
        { id: 7, value: "HOWTO & STYLE" },
        { id: 8, value: "MOTIVATIONAL" },
        { id: 9, value: "FOOD AND COOKING" },
        { id: 10, value: "MUSIC" },
        { id: 11, value: "NEWS & POLITICS" },
        { id: 12, value: "NONPROFITS & ACTIVISM" },
        { id: 13, value: "PEOPLE & BLOGS" },
        { id: 14, value: "NATURE AND ANIMALS" },
        { id: 15, value: "SCIENCE & TECHNOLOGY" },
        { id: 16, value: "SPORTS" },
        { id: 17, value: "ADVENTURE" },
        { id: 18, value: "PARENTING AND KIDS" },
    ];

    const filteredCategories = catogary.filter(cat =>
        cat.value.toLowerCase().includes(categorySearch.toLowerCase())
    );

    const toggleButtons = [
        { id: 'private', label: 'Private', icon: <Lock size={18} />, state: isCheckedPrivate, onChange: handleCheckboxPrivate },
        { id: 'comment', label: 'Allow comments', icon: <MessageCircle size={18} />, state: isCheckedComment, onChange: handleCheckboxComment },
        { id: 'download', label: 'Allow viewer to download video', icon: <Download size={18} />, state: isCheckedDownload, onChange: handleCheckboxDownload },
    ];

    if (loading && !draftVideos) {
        return (
            <div className="absolute bottom-0 w-full h-full bg-white dark:bg-black flex justify-center items-center">
                <ClipLoader color="#000" size={30} />
            </div>
        );
    }

    if (!draftVideos) {
        return (
            <div className="absolute bottom-0 w-full h-full bg-white dark:bg-black flex justify-center items-center">
                <div className="text-center">
                    <p className="text-xl font-bold dark:text-white">No draft video found</p>
                    <button
                        onClick={() => navigate("/draft-videos")}
                        className="mt-4 rounded-full text-[12px] px-10 h-7 bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="absolute bottom-0 w-full h-full bg-[#fff] dark:bg-black overflow-hidden">

            {activeStep === 0 &&
                <div className="sticky top-0 z-50">
                    <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                        <IoMdArrowBack
                            onClick={() => navigate("/draft-videos")}
                            className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                        />
                        <p className="text-center font-bold text-[16px] text-black flex-grow">Edit Draft</p>
                        <div
                            onClick={handleDeleteDraft}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full h-7 w-7 p-[2px] cursor-pointer flex justify-center items-center bg-red-500 hover:bg-red-600"
                        >
                            <FaTrashAlt className="text-[12px] text-white" />
                        </div>
                    </div>
                </div>
            }

            {!(activeStep == 1) && (
                <div className="h-[50%] flex flex-col items-center justify-center">
                    <div
                        className="h-[38vh] w-[55%] rounded-md border border-gray-400 dark:border-gray-800 flex flex-col items-center justify-center relative bg-black"
                        onMouseEnter={() => setShowControls(true)}
                        onMouseLeave={() => setShowControls(false)}
                    >
                        {draftVideos.video_url ? (
                            <>
                                <video
                                    ref={videoRef}
                                    src={draftVideos.video_url}
                                    muted={isMuted}
                                    controls={false}
                                    disableRemotePlayback
                                    disablePictureInPicture
                                    loop
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    className="h-full w-full object-cover rounded-md"
                                />
                                {showControls && (
                                    <div className="absolute bottom-2 left-2 flex gap-2 z-10">
                                        <button
                                            onClick={handlePlayPause}
                                            className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                                        >
                                            {isPlaying ? (
                                                <IoIosPause className="text-[15px] text-black dark:text-white" />
                                            ) : (
                                                <IoMdPlay className="text-[15px] ml-[3px] text-black dark:text-white" />
                                            )}
                                        </button>
                                        <button
                                            onClick={handleMuteUnmute}
                                            className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                                        >
                                            {isMuted ? (
                                                <HiMiniSpeakerXMark className="text-[15px] text-black dark:text-white" />
                                            ) : (
                                                <HiMiniSpeakerWave className="text-[15px] text-black dark:text-white" />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-full text-[10px] flex items-center justify-center text-gray-400 dark:text-gray-600">
                                Loading video...
                            </div>
                        )}
                    </div>
                </div>
            )}

            {draftVideos.video_url && (
                <>
                    <form className="w-full border-t border-gray-400 dark:border-gray-800">
                        {activeStep === 0 &&
                            <div className="flex w-full items-center justify-center cursor-pointer mt-3">
                                <p className="text-[16px] font-bold dark:text-white">
                                    Your Caption
                                </p>
                            </div>
                        }
                        {activeStep === 0 && (
                            <div className="absolute pb-6 overflow-y-scroll w-[100%] mt-2 px-2 left-1/2 transform -translate-x-1/2">
                                <div className="relative">
                                    <textarea
                                        rows="4"
                                        maxLength={200}
                                        value={description}
                                        onChange={handleChange}
                                        placeholder="Type caption here..."
                                        className="w-full p-2 text-[14px] focus:outline-none resize-none bg-gray-100 rounded-md"
                                    />
                                    <p className="text-[12px] absolute right-0 -bottom-3">
                                        {count}/200
                                    </p>
                                </div>
                            </div>
                        )}
                        {activeStep === 1 && (
                            <div className="w-full h-[-webkit-fill-available] overflow-scroll no-scrollbar">
                                {/* Header */}
                                <div className="sticky top-0 z-50 bg-white dark:bg-black">
                                    <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                                        <IoMdArrowBack
                                            onClick={() => setActiveStep(0)}
                                            className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                                        />
                                        <p className="text-center font-bold text-[16px] text-black flex-grow">Draft Upload Settings</p>
                                    </div>
                                </div>

                                {/* Add category button */}
                                {/* <div
                                    onClick={() => setShowCategoryModal(true)}
                                    className="flex items-center justify-between px-4 py-4 mb-3 mx-4 mt-4 bg-white dark:bg-[#1a1a1a] border-2 border-[#BDE6FF] rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-[#222]"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-6 h-6">
                                            <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[14px] font-semibold dark:text-white">Add category</p>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                {selectedInterest || "Select the best category for your video."}
                                            </p>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div> */}

                                {/* Toggle switches */}
                                {toggleButtons.map((button) => (
                                    <div key={button.id} className="flex justify-between items-center px-4 py-4 bg-white dark:bg-[#1a1a1a]">
                                        <div className="flex items-center gap-3">
                                            <div className="text-black dark:text-white text-[18px]">
                                                {button.icon}
                                            </div>
                                            <p className="text-[14px] font-medium text-black dark:text-white">{button.label}</p>
                                        </div>
                                        <label className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center">
                                            <input
                                                type="checkbox"
                                                name={button.id}
                                                className="sr-only"
                                                checked={button.state}
                                                onChange={button.onChange}
                                            />
                                            <span className={`slider flex h-[22px] w-[42px] items-center rounded-full p-1 duration-200 
                                                ${button.state ? "bg-[#BDE6FF]" : "bg-[#D1D1D6]"}`}
                                            >
                                                <span className={`dot h-[16px] w-[16px] rounded-full bg-white duration-200 shadow-md 
                                                    ${button.state ? "translate-x-[18px]" : ""}`}
                                                />
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </form>

                    {/* Bottom Navigation */}
                    <div className="absolute bottom-0 w-full flex gap-3 px-4 py-3 bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800">
                        {activeStep === 1 && (
                            <>
                                <button
                                    onClick={handleUpdateDraft}
                                    disabled={isUpdating || (uploadProgress > 0 && uploadProgress < 100)}
                                    className={`flex-1 rounded-xl text-[14px] font-semibold h-10 border-2 relative overflow-hidden ${isUpdating || (uploadProgress > 0 && uploadProgress < 100)
                                        ? "bg-gray-200 text-black border-[#BDE6FF] cursor-not-allowed"
                                        : "bg-white dark:bg-black text-black dark:text-white border-gray-600 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900"
                                        }`}
                                >
                                    {(isUpdating || (uploadProgress > 0 && uploadProgress < 100)) && uploadProgress > 0 && (
                                        <div
                                            className="absolute left-0 top-0 h-full bg-[#BDE6FF] transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    )}
                                    <span className="relative z-10">
                                        {isUpdating || (uploadProgress > 0 && uploadProgress < 100)
                                            ? uploadProgress > 0
                                                ? `Updating... ${uploadProgress}%`
                                                : "Updating..."
                                            : "Update Draft"
                                        }
                                    </span>
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isPublishing}
                                    className={`flex-1 rounded-xl text-[14px] font-semibold h-10 relative overflow-hidden ${isPublishing ? "bg-gray-200 text-black border-2 border-[#BDE6FF] cursor-not-allowed" : "bg-[#BDE6FF] hover:bg-[#9dd5f0]"
                                        } text-black`}
                                >
                                    {isPublishing && uploadProgress > 0 && (
                                        <div
                                            className="absolute left-0 top-0 h-full bg-[#BDE6FF] transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    )}
                                    <span className="relative z-10">
                                        {isPublishing ? `Publishing... ${uploadProgress}%` : "Publish"}
                                    </span>
                                </button>
                            </>
                        )}
                        {activeStep === 0 && (
                            <button
                                onClick={() => setActiveStep(1)}
                                className="w-full rounded-xl bg-[#BDE6FF] hover:bg-[#9dd5f0] text-[14px] font-semibold h-10 text-black"
                            >
                                Next
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* Category Selection Modal */}
            {showCategoryModal && (
                <div className="absolute inset-0 z-50 bg-white dark:bg-black">
                    <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="sticky top-0 z-50">
                            <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                                <IoMdArrowBack
                                    onClick={() => setShowCategoryModal(false)}
                                    className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                                />
                                <p className="text-center font-bold text-[16px] text-black flex-grow">Add category</p>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={categorySearch}
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                    className="w-full px-4 py-2 pl-4 pr-10 bg-gray-100 dark:bg-gray-900 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-300 dark:text-white"
                                />
                                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* Category List */}
                        <div className="flex-1 overflow-y-auto">
                            {filteredCategories.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        setSelectedInterest(item.value);
                                        setShowCategoryModal(false);
                                    }}
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900"
                                >
                                    <p className="text-[14px] dark:text-white">{item.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Done Button */}
                        <div className="p-4">
                            <button
                                onClick={() => setShowCategoryModal(false)}
                                className="w-full rounded-xl bg-[#BDE6FF] hover:bg-[#9dd5f0] text-[14px] font-semibold h-10 text-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPrivatePopup && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white w-[80%] max-w-sm dark:bg-gray-900 px-6 py-4 rounded-xl shadow-lg text-center">
                        <div className="mb-3">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <FolderLock className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <p className="text-[18px] font-bold mb-2 text-gray-900 dark:text-white">Private Folder Not Created</p>
                        <p className="mb-6 text-[13px] text-gray-600 dark:text-gray-300">
                            You need to create a private folder before you can upload private videos.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                className="flex-1 text-[14px] bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => {
                                    setShowPrivatePopup(false);
                                    setIsCheckedPrivate(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="flex-1 text-[14px] bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => {
                                    setShowPrivatePopup(false);
                                    navigate("/account/private/empty");
                                }}
                            >
                                Create Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DraftVideosDetails;